import { useState } from 'react';
import { Home, LifeBuoy } from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  ErrorState,
  type ErrorAction,
  type ErrorHelpLink,
} from './error-state';
import { useLanguage, useTranslation } from '../i18n/useTranslation';

export type ErrorPageNavigation = {
  secondaryAction: ErrorAction;
  helpLinks: ErrorHelpLink[];
};

type ErrorPageProps = {
  eventId?: string;
  resetError?: () => void;
  variant?: 'in-layout' | 'standalone';
  errorNavigation?: ErrorPageNavigation;
};

type ServerErrorStateProps = ErrorPageProps & {
  capturedAt: Date;
  primaryAction: { label: string; onClick: () => void };
  secondaryAction: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  helpLinks: ErrorHelpLink[];
};

function ServerErrorState({
  eventId,
  variant = 'in-layout',
  capturedAt,
  primaryAction,
  secondaryAction,
  helpLinks,
}: ServerErrorStateProps) {
  const { t } = useTranslation('common');
  const { language } = useLanguage();
  const capturedTime = new Intl.DateTimeFormat(language, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(capturedAt);

  return (
    <ErrorState
      code="500"
      accent="amber"
      eyebrow={t('error.eyebrow')}
      title={t('error.title')}
      description={t('error.description')}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      incident={
        eventId
          ? {
              title: t('error.incident.title'),
              description: t('error.incident.description'),
              referenceLabel: t('error.incident.reference'),
              reference: eventId,
              timeLabel: t('error.incident.time'),
              time: capturedTime,
            }
          : undefined
      }
      helpTitle={t('error.helpTitle')}
      helpLinks={helpLinks}
      variant={variant}
    />
  );
}

function StandaloneErrorPage({
  eventId,
  resetError,
  capturedAt,
}: ErrorPageProps & { capturedAt: Date }) {
  const { t } = useTranslation('common');
  const { language } = useLanguage();
  const retry = resetError ?? (() => window.location.reload());

  return (
    <ServerErrorState
      eventId={eventId}
      capturedAt={capturedAt}
      variant="standalone"
      primaryAction={{ label: t('error.retry'), onClick: retry }}
      secondaryAction={{
        label: t('error.backToWebsite'),
        href: `https://bill-alps.ch/${language}/`,
      }}
      helpLinks={[
        {
          href: '/dashboard',
          label: t('error.links.app.label'),
          description: t('error.links.app.description'),
          icon: <Home aria-hidden="true" />,
        },
        {
          href: 'mailto:info@bill-alps.ch',
          label: t('error.links.support.label'),
          description: t('error.links.support.description'),
          icon: <LifeBuoy aria-hidden="true" />,
        },
      ]}
    />
  );
}

function InLayoutErrorPage({
  eventId,
  resetError,
  capturedAt,
  errorNavigation,
}: ErrorPageProps & { capturedAt: Date }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const retry = resetError ?? (() => window.location.reload());

  function handleDashboard() {
    navigate('/dashboard');
    resetError?.();
  }

  return (
    <ServerErrorState
      eventId={eventId}
      capturedAt={capturedAt}
      primaryAction={{ label: t('error.retry'), onClick: retry }}
      secondaryAction={
        errorNavigation?.secondaryAction ?? {
          label: t('error.backToDashboard'),
          onClick: handleDashboard,
        }
      }
      helpLinks={
        errorNavigation?.helpLinks ?? [
          {
            href: '/dashboard',
            label: t('error.links.app.label'),
            description: t('error.links.app.description'),
            icon: <Home aria-hidden="true" />,
          },
          {
            href: '/support',
            label: t('error.links.support.label'),
            description: t('error.links.support.description'),
            icon: <LifeBuoy aria-hidden="true" />,
          },
        ]
      }
    />
  );
}

export function ErrorPage({
  eventId,
  resetError,
  variant = 'in-layout',
  errorNavigation,
}: ErrorPageProps) {
  const [capturedAt] = useState(() => new Date());

  if (variant === 'standalone') {
    return (
      <StandaloneErrorPage
        eventId={eventId}
        resetError={resetError}
        capturedAt={capturedAt}
      />
    );
  }

  return (
    <InLayoutErrorPage
      eventId={eventId}
      resetError={resetError}
      capturedAt={capturedAt}
      errorNavigation={errorNavigation}
    />
  );
}
